import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiUserPlus, FiEye, FiEdit2, FiTrash2, FiKey, FiShield, FiSearch, FiPower } from "react-icons/fi";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import { usePermission } from "../../context/PermissionContext";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const StatusBadge = ({ status }) => (
  <span
    className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
      status === "active" ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"
    }`}
  >
    <span className={`w-1.5 h-1.5 rounded-full ${status === "active" ? "bg-green-500" : "bg-gray-400"}`} />
    {status === "active" ? "Active" : "Inactive"}
  </span>
);

const StaffList = () => {
  const { hasPermission } = usePermission();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [staff, setStaff] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 10, ...(search ? { search } : {}) });
      const res = await fetch(`${baseUrl}/staff/list?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.status) {
        setStaff(result.data || []);
        setPagination(result.pagination || null);
      } else {
        toast.error(result.message || "Can't fetch staff list");
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, search, token]);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const toggleStatus = async (member) => {
    const nextStatus = member.status === "active" ? "inactive" : "active";
    try {
      const res = await fetch(`${baseUrl}/staff/status/${member.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: nextStatus }),
      });
      const result = await res.json();
      if (!result.status) throw new Error(result.message || "Failed to update status");
      toast.success(`Staff ${nextStatus === "active" ? "activated" : "deactivated"}`);
      fetchStaff();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const removeStaff = async (member) => {
    if (!window.confirm(`Delete staff "${member.name || member.email}"? This can't be undone.`)) return;
    try {
      const res = await fetch(`${baseUrl}/staff/remove/${member.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (!result.status) throw new Error(result.message || "Failed to delete staff");
      toast.success("Staff deleted");
      fetchStaff();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-5 sm:p-8 space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                <FiShield size={20} className="text-indigo-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800">Staff</h2>
                <p className="text-sm text-gray-400 mt-0.5">Manage staff logins and their roles</p>
              </div>
            </div>
            {hasPermission("staff", "create") && (
              <button
                onClick={() => navigate("/staff/create")}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition"
              >
                <FiUserPlus size={16} /> Add Staff
              </button>
            )}
          </div>

          <div className="relative max-w-sm">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {loading ? (
              <div className="flex justify-center items-center py-16">
                <div className="w-7 h-7 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : staff.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <FiShield size={32} className="text-gray-300 mb-3" />
                <p className="text-sm">No staff yet. Add one above.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs text-gray-400 uppercase tracking-wide">
                      <th className="px-5 py-3">Name</th>
                      <th className="px-5 py-3">Email</th>
                      <th className="px-5 py-3">Role</th>
                      <th className="px-5 py-3">Status</th>
                      <th className="px-5 py-3">Last Login</th>
                      <th className="px-5 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {staff.map((member) => (
                      <tr key={member.id} className="hover:bg-gray-50 transition">
                        <td className="px-5 py-3.5 font-medium text-gray-800">{member.name || "—"}</td>
                        <td className="px-5 py-3.5 text-gray-600">{member.email}</td>
                        <td className="px-5 py-3.5 text-gray-600">{member.roleName || <span className="text-amber-500">Unassigned</span>}</td>
                        <td className="px-5 py-3.5"><StatusBadge status={member.status} /></td>
                        <td className="px-5 py-3.5 text-gray-400 text-xs">
                          {member.lastLoginAt ? new Date(member.lastLoginAt).toLocaleString() : "Never"}
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center justify-end gap-1.5">
                            {hasPermission("staff", "view") && (
                              <button onClick={() => navigate(`/staff/${member.id}`)} title="View" className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-blue-600">
                                <FiEye size={14} />
                              </button>
                            )}
                            {hasPermission("staff", "edit") && (
                              <>
                                <button onClick={() => navigate(`/staff/${member.id}/edit`)} title="Edit" className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-amber-600">
                                  <FiEdit2 size={14} />
                                </button>
                                <button onClick={() => navigate(`/staff/${member.id}/edit`, { state: { focus: "permissions" } })} title="Manage Permissions" className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-blue-600">
                                  <FiShield size={14} />
                                </button>
                                <button onClick={() => toggleStatus(member)} title={member.status === "active" ? "Deactivate" : "Activate"} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-indigo-600">
                                  <FiPower size={14} />
                                </button>
                                <button onClick={() => navigate(`/staff/${member.id}/edit`, { state: { focus: "password" } })} title="Reset Password" className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-purple-600">
                                  <FiKey size={14} />
                                </button>
                              </>
                            )}
                            {hasPermission("staff", "delete") && (
                              <button onClick={() => removeStaff(member)} title="Delete" className="p-2 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600">
                                <FiTrash2 size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {pagination && pagination.lastPage > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-4 py-2 text-sm bg-white border border-gray-200 rounded-lg disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-sm text-gray-500">Page {pagination.currentPage} of {pagination.lastPage}</span>
              <button
                disabled={page >= pagination.lastPage}
                onClick={() => setPage((p) => p + 1)}
                className="px-4 py-2 text-sm bg-white border border-gray-200 rounded-lg disabled:opacity-40"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffList;
